import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import { firstValueFrom } from "rxjs";

export const SERIAL_SCHEMA = {
    type: "object",
    additionalProperties: false,
    properties: {
        serial: { type: ["string", "null"] },
        best_source: { type: "string", enum: ["label", "settings", "unknown"] },
        candidates: {
            type: "array",
            items: {
                type: "object",
                additionalProperties: false,
                properties: {
                    value: { type: "string" },
                    confidence: { type: "number", minimum: 0, maximum: 1 },
                    evidence: { type: "string" }
                },
                required: ["value", "confidence", "evidence"]
            }
        },
        notes: { type: "string" }
    },
    required: ["serial", "best_source", "candidates", "notes"]
} as const;


export interface SerialExtractionResult {
    serial: string | null;
    best_source: "label" | "settings" | "unknown";
    candidates: { value: string; confidence: number; evidence: string }[];
    notes: string;
}

@Injectable({ providedIn: "root" })
/**
 * Service for extracting hardware serial numbers from images using OpenAI's vision API.
 *
 * This service analyzes image files (photos or screenshots) to identify and extract hardware serial numbers.
 * It uses OpenAI's GPT-4o-mini model with structured output to ensure reliable, formatted results.
 *
 * @remarks
 * - The service requires a valid OpenAI API key configured in the environment.
 * - Images are converted to data URLs before being sent to the API.
 * - Serial number extraction follows strict validation rules to prevent guessing or inferring missing characters.
 * - Confidence scoring is conservative: set to 1.0 only when the serial is perfectly readable with no ambiguity,
 *   and capped at 0.60 if any character is ambiguous (e.g., O/0, I/1, V/Y).
 * - When multiple images are provided, evidence is combined to identify the best serial number.
 * - UPC codes, model numbers, and part numbers are ignored unless explicitly labeled as the serial.
 * - The service includes utility methods for file-to-data-URL conversion and optional image blurring for testing.
 *
 * @example
 * ```typescript
 * constructor(private serialService: OpenAiSerialExtractorService) {}
 *
 * async extractSerial() {
 *   const files = [imageFile1, imageFile2];
 *   const result = await this.serialService.extractSerialFromImages(files);
 *   if (result.serial) {
 *     console.log(`Found serial: ${result.serial} (confidence: ${result.confidence})`);
 *   } else {
 *     console.log(`No serial found: ${result.notes}`);
 *   }
 * }
 * ```
 */
export class OpenAiSerialExtractorService {
    private readonly apiKey = environment.openAiApiKey ?? "";

    private readonly endpoint = "https://api.openai.com/v1/responses";
    private readonly model = "gpt-4o-mini";

    constructor(private http: HttpClient) { }

    /**
     * Extracts hardware serial numbers from one or more image files using OpenAI's vision API.
     *
     * @param files - An array of image files (Photo/screenshots) to analyze for serial numbers.
     * @returns A promise that resolves to a {@link SerialExtractionResult} containing the extracted serial number,
     *          confidence level, any candidate serials, and notes explaining the extraction result.
     *
     * @throws {Error} If the OpenAI API key is not configured.
     * @throws {Error} If the OpenAI API response does not contain valid structured output.
     * @throws {SyntaxError} If the structured output cannot be parsed as JSON.
     *
     * @remarks
     * - The method converts image files to data URLs before sending to the OpenAI API.
     * - Serial numbers are extracted with strict validation rules to avoid guessing or inferring missing characters.
     * - Confidence is only set to 1.0 when the serial is perfectly readable with no ambiguity.
     * - Confidence is capped at 0.60 if any character is ambiguous (e.g., O/0, I/1, V/Y).
     * - If multiple images are provided, the method combines evidence across them to identify the best serial.
     * - UPC codes, model numbers, and part numbers are ignored unless explicitly labeled as the serial.
     *
     * @example
     * const files = [imageFile1, imageFile2];
     * const result = await service.extractSerialFromImages(files);
     * if (result.serial) {
     *   console.log(`Serial: ${result.serial} (confidence: ${result.confidence})`);
     * } else {
     *   console.log(`No serial found: ${result.notes}`);
     * }
     */
    async extractSerialFromImages(files: File[]): Promise<SerialExtractionResult> {
        if (!this.apiKey) throw new Error("Missing OpenAI API key.");

        // for testing if needed
        // const blurredImages = await Promise.all(files.map(f => this.blurImageFile(f)));

        const images = await Promise.all(files.map(f => this.fileToDataUrl(f)));

        const instructions = `You extract hardware serial numbers from photos/screenshots.

Task:
- Look for a serial number on product labels, stickers, packaging labels, or device "About" screens.
- Prefer fields labeled “Serial”, “Serial Number”, or “(S) Serial No.”
- If multiple images are provided, combine evidence across them and pick the best serial.

Output rules:
- Return ONLY valid JSON matching the provided schema.
- Serial must be uppercase, with no spaces.
- If there are multiple plausible serials, include them as candidates with confidence (0 to 1), and set serial to the best one.
- If you cannot confidently find any serial, set serial to null and explain briefly in notes.

Validation hints:
- Serial numbers are typically alphanumeric and often 10–17 chars (do not invent/guess).
- Ignore UPC, model numbers, and part numbers unless explicitly labeled as the serial.

CRITICAL:
- If the serial number is not clearly readable, return serial = null.
- Do NOT guess. Do NOT infer missing characters.
- Confidence must be <= 0.60 unless every character is clearly legible.
- Confidence must be 1.0 ONLY if the serial is perfectly readable with no ambiguity.
- If any character could be more than one option (e.g., O/0, I/1, V/Y), set serial=null and explain in notes.
- Always include evidence describing exactly where you read it (e.g., "label line '(S) Serial No.'").
`;

        const body: any = {
            model: this.model,
            instructions,
            input: [
                {
                    role: "user",
                    content: images.map((dataUrl) => ({
                        type: "input_image",
                        image_url: dataUrl
                    }))
                }
            ],
            text: {
                format: {
                    type: "json_schema",
                    name: "serial_extraction",
                    strict: true,
                    schema: SERIAL_SCHEMA
                }
            }
        };

        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.apiKey}`,
            "Content-Type": "application/json"
        });

        const resp = await firstValueFrom(this.http.post<any>(this.endpoint, body, { headers }));

        // Responses API: the model’s structured JSON is typically at output[0].content[0].text
        // Depending on SDK/version, you may see it in different places—this is the common shape.
        const text = resp?.output?.[0]?.content?.[0]?.text;
        if (!text) {
            throw new Error("OpenAI response missing structured output text.");
        }

        return JSON.parse(text) as SerialExtractionResult;
    }

    private fileToDataUrl(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => resolve(String(reader.result));
            reader.readAsDataURL(file);
        });
    }

    /**
     * For testing purposes, blurs an image file using canvas and returns a new File object.
     * @param file - The image file to blur
     * @param blurPx - The blur radius in pixels (default: 3)
     * @returns A promise that resolves to a new File with the blurred image
     * @throws Error if the canvas 2D context cannot be obtained
     */
    async blurImageFile(file: File, blurPx = 3): Promise<File> {
        const img = await this.fileToImage(file);

        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('No 2D context');

        // Apply blur while drawing
        (ctx as any).filter = `blur(${blurPx}px)`;
        ctx.drawImage(img, 0, 0);

        const blob = await new Promise<Blob>((resolve) =>
            canvas.toBlob((b) => resolve(b!), file.type || 'image/png', 0.95)
        );

        return new File([blob], file.name.replace(/\.\w+$/, '') + `_blur${blurPx}.png`, { type: blob.type });
    }

    fileToImage(file: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
            img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
            img.src = url;
        });
    }
}

export const SERIAL_IMAGE_BLURRED = {
    "id": "resp_0dd7678f9df44d41006986491eb5b481a3b80756cb38fec24b",
    "object": "response",
    "created_at": 1770408222,
    "status": "completed",
    "background": false,
    "billing": {
        "payer": "developer"
    },
    "completed_at": 1770408225,
    "error": null,
    "frequency_penalty": 0,
    "incomplete_details": null,
    "instructions": "You extract hardware serial numbers from photos/screenshots.\n\nTask:\n- Look for a serial number on product labels, stickers, packaging labels, or device \"About\" screens.\n- Prefer fields labeled “Serial”, “Serial Number”, or “(S) Serial No.”\n- If multiple images are provided, combine evidence across them and pick the best serial.\n\nOutput rules:\n- Return ONLY valid JSON matching the provided schema.\n- Serial must be uppercase, with no spaces.\n- If there are multiple plausible serials, include them as candidates with confidence (0 to 1), and set serial to the best one.\n- If you cannot confidently find any serial, set serial to null and explain briefly in notes.\n\nValidation hints:\n- Serial numbers are typically alphanumeric and often 10–17 chars (do not invent/guess).\n- Ignore UPC, model numbers, and part numbers unless explicitly labeled as the serial.\n\nCRITICAL:\n- If the serial number is not clearly readable, return serial = null.\n- Do NOT guess. Do NOT infer missing characters.\n- Confidence must be <= 0.60 unless every character is clearly legible.\n- Confidence must be 1.0 ONLY if the serial is perfectly readable with no ambiguity.\n- If any character could be more than one option (e.g., O/0, I/1, V/Y), set serial=null and explain in notes.\n- Always include evidence describing exactly where you read it (e.g., \"label line '(S) Serial No.'\").\n",
    "max_output_tokens": null,
    "max_tool_calls": null,
    "model": "gpt-4o-mini-2024-07-18",
    "output": [
        {
            "id": "msg_0dd7678f9df44d41006986492075bc81a3b65dce80e60ff438",
            "type": "message",
            "status": "completed",
            "content": [
                {
                    "type": "output_text",
                    "annotations": [],
                    "logprobs": [],
                    "text": "{\"serial\":null,\"best_source\":\"unknown\",\"candidates\":[],\"notes\":\"The image is too blurry to clearly read any serial number. There are multiple alphanumeric characters visible, but no clear serial number can be extracted.\"}"
                }
            ],
            "role": "assistant"
        }
    ],
    "parallel_tool_calls": true,
    "presence_penalty": 0,
    "previous_response_id": null,
    "prompt_cache_key": null,
    "prompt_cache_retention": null,
    "reasoning": {
        "effort": null,
        "summary": null
    },
    "safety_identifier": null,
    "service_tier": "default",
    "store": true,
    "temperature": 1,
    "text": {
        "format": {
            "type": "json_schema",
            "description": null,
            "name": "serial_extraction",
            "schema": {
                "type": "object",
                "additionalProperties": false,
                "properties": {
                    "serial": {
                        "type": [
                            "string",
                            "null"
                        ]
                    },
                    "best_source": {
                        "type": "string",
                        "enum": [
                            "label",
                            "settings",
                            "unknown"
                        ]
                    },
                    "candidates": {
                        "type": "array",
                        "items": {
                            "type": "object",
                            "additionalProperties": false,
                            "properties": {
                                "value": {
                                    "type": "string"
                                },
                                "confidence": {
                                    "type": "number",
                                    "minimum": 0,
                                    "maximum": 1
                                },
                                "evidence": {
                                    "type": "string"
                                }
                            },
                            "required": [
                                "value",
                                "confidence",
                                "evidence"
                            ]
                        }
                    },
                    "notes": {
                        "type": "string"
                    }
                },
                "required": [
                    "serial",
                    "best_source",
                    "candidates",
                    "notes"
                ]
            },
            "strict": true
        },
        "verbosity": "medium"
    },
    "tool_choice": "auto",
    "tools": [],
    "top_logprobs": 0,
    "top_p": 1,
    "truncation": "disabled",
    "usage": {
        "input_tokens": 14569,
        "input_tokens_details": {
            "cached_tokens": 0
        },
        "output_tokens": 47,
        "output_tokens_details": {
            "reasoning_tokens": 0
        },
        "total_tokens": 14616
    },
    "user": null,
    "metadata": {}
};